import React, { useEffect, useState } from 'react'
import LayoutSidebar from '../components/LayoutSidebar'
import { useRequest } from '../hooks/UseRequest'
import { useDispatch, useSelector } from 'react-redux'
import Select, { components as reactSelectComponents } from 'react-select'
import { reactSelectClassNames } from '../helpers/react-select-styles';
import { setNotification } from '../redux/notificationSlice';
import { setModal } from '../redux/modalSlice'
import AsyncSelect from 'react-select/async'
import { selectPortfolios } from '../redux/portfolioSlice'
import { selectDropdownOptions, selectDropdowns } from '../redux/dropdownSlice'
import { Controller } from 'react-hook-form'
import { setChangeMade } from '../redux/paymentSchedulePanelSlice'

// Scenario option testids for e2e; slug must match tests/e2e/helpers/test-data.js.
const scenarioOptionTestId = (label) =>
  `test-setup-option-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`

const ScenarioOption = (props) => (
  <reactSelectComponents.Option
    {...props}
    innerProps={{ ...props.innerProps, 'data-testid': scenarioOptionTestId(props.data.label) }}
  />
)

export default function TestSetup({ type }) {
  const request = useRequest();
  const rangeRequest = useRequest();
  const [leadType, setLeadType] = useState(null);
  const [loanType, setLoanType] = useState(null);
  const [cron, setCron] = useState(null);
  const [customerIds, setSetCustomerIds] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const portfolios = useSelector(selectPortfolios)
  const dropdowns = useSelector(selectDropdowns)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const [portfolioOptions, setPortfolioOptions] = useState([]);
  const [payFrequencyOptions, setPayFrequencyOptions] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [selectedFrequency, setSelectedFrequency] = useState(null);
  const [selectedOriginationDate, setSelectedOriginationDate] = useState(null);
  const [selectedPrincipalAmount, setSelectedPrincipalAmount] = useState('');
  const [selectedApr, setSelectedApr] = useState('');
  const [selectedRewardPoints, setSelectedRewardPoints] = useState('');
  const [principalRange, setPrincipalRange] = useState(null);
  const [routingStatusOptions, setRoutingStatusOptions] = useState([]);
  const [selectedRoutingStatus, setSelectedRoutingStatus] = useState(null);

  const payFrequency = dropdowns.data.filter((o) => o.code_key === 'pay_frequency')[0]
  const routingNumberStatus = dropdowns.data.filter((o) => o.code_key === 'routing_number_status')[0]

  useEffect(() => {
    if (portfolios) {
      const portfolioData = portfolios.data.filter((p) => p.id !== null).map((p) => ({ label: p.name, value: p.id, code_key: p.code_key }))
      setPortfolioOptions(portfolioData)
      setSelectedPortfolio(portfolioData.filter((p) => p.code_key === 'LEN')?.[0])
    }
  }, [portfolios])

  useEffect(() => {
    if (payFrequency) {
      const options = dropdownOptions.filter((o) => o.dropdown_id === payFrequency.id && o.code_key !== 'pay_frequency_daily').map((o) => ({ label: o.name, value: o.id, code_key: o.code_key }))
      setPayFrequencyOptions(options)

      setSelectedFrequency(options.filter((p) => p.code_key === 'pay_frequency_biweekly')?.[0])
    }
  }, [payFrequency])

  useEffect(() => {
    if (routingNumberStatus) {
      const options = dropdownOptions
        .filter((o) => o.dropdown_id === routingNumberStatus.id)
        .map((o) => ({ label: o.name, value: o.id, code_key: o.code_key }))
      setRoutingStatusOptions(options)
      setSelectedRoutingStatus(options.filter((p) => p.code_key === 'routing_number_status_verified')?.[0])
    }
  }, [routingNumberStatus])

  useEffect(() => {
    if (!selectedPortfolio || !selectedFrequency) return
    const isReturningCustomer = (loanType || '').toLowerCase().includes('returning') ||
                                (leadType || '').toLowerCase().includes('returning')
    rangeRequest.setEndpoint(
      `${process.env.REACT_APP_API_DOMAIN}/test_data/principal_range?portfolioId=${selectedPortfolio.value}&frequency=${selectedFrequency.code_key}&isReturningCustomer=${isReturningCustomer}`
    )
    rangeRequest.get()
      .then((response) => setPrincipalRange(response.data))
      .catch(() => setPrincipalRange(null))
  }, [selectedPortfolio, selectedFrequency, loanType, leadType])

  const submitCreate = async (type) => {
    setSubmitting(true)
    const args = type === 'LeadStateTestSeeder' ? leadType : loanType
    if (!args) {
      setSubmitting(false)
      return dispatch(setNotification({ type: 'error', body: 'Please choose a type from the dropdown.' }))
    }
    const extraOptions = {
      frequency: selectedFrequency.code_key,
      portfolioId: selectedPortfolio.value,
      originationDate: selectedOriginationDate,
      principalAmount: selectedPrincipalAmount === '' ? null : parseInt(selectedPrincipalAmount, 10),
      apr: selectedApr === '' ? null : parseFloat(selectedApr),
      rewardPoints: selectedRewardPoints === '' ? null : parseInt(selectedRewardPoints, 10),
      bankRoutingNumberStatus: selectedRoutingStatus?.code_key ?? 'routing_number_status_verified',
    }
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/test_data/create_test_data`)
    try {
      const response = await request.post({ type: type, args: args, extraOptions })
      if (response.data.error) {
        setSubmitting(false)
        return dispatch(setNotification({ type: 'error', body: response.data.error }))
      }
      // A rejected seed (an unrecognised scope or seeder) comes back 200 carrying only a
      // `message`, with no `error` key — so an absent customer_id is the only reliable
      // signal that nothing was created. Without this the success modal renders and links
      // to /customer/undefined.
      if (!response.data.customer_id) {
        setSubmitting(false)
        return dispatch(setNotification({ type: 'error', body: response.data.message || 'Test data creation failed.' }))
      }
      dispatch(
        setModal({ is_active: true, title: 'created', content: response.data.message, type: 'test_data_modal', options: { url: response.data.url, customer_name: response.data.customer_name, customer_id: response.data.customer_id } })
      )
      setSubmitting(false)
    } catch (error) {
      setSubmitting(false)
      return dispatch(setNotification({ type: 'error', body: JSON.parse(error.request.response).message }))
    }
  }

  const submitExecuteCron = async () => {
    setSubmitting(true)
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/test_data/execute_cron/${cron.value}`)
    try {
      const response = await request.get();
      setSubmitting(false)
      return dispatch(setNotification({ type: 'success', body: `The cron job ${cron.value} is currently executing. Please check the result after some time.` }))
    } catch (error) {
      setSubmitting(false)
      return dispatch(setNotification({ type: 'error', body: JSON.parse(error.request.response).message }))
    }
  }

  const loadCrons = async (inputValue) => {
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/test_data/get_cron`)
    return request.get().then((response) => response.data);
  };

  const syncCustomers = async () => {
    setSubmitting(true)
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/test_data/sync_customers`)
    try {
      const response = await request.post({ type: type, customer_ids: customerIds })
      dispatch(
        setModal({ is_active: true, title: 'created', content: response.data.message, type: 'test_data_modal', options: { url: response.data.url, customer_name: response.data.customer_name, customer_id: response.data.customer_id } })
      )
      setSubmitting(false)
    } catch (error) {
      setSubmitting(false)
      return dispatch(setNotification({ type: 'error', body: JSON.parse(error.request.response).message }))
    }
  };

  const leadStates = [
    { label: 'Pending SSV', value: 'pendingSsv' },
    { label: 'Pending SSV With Ribbit Login', value: 'pendingSsvWithRibbitLogin' },
    { label: 'Pending SSV With Drivers License Verification Potential', value: 'pendingSsvWithDriversLicenseVerificationPotential' },
    { label: 'Pending SSV With Drivers License Verification Required', value: 'pendingSsvWithDriversLicenseVerificationRequired' },
    { label: 'Pending SSV Returning Customer', value: 'pendingSsvReturningCustomer' },
    { label: 'Completed SSV with Error', value: 'completedSsvWithError' },
    { label: 'Started SSV Not Finalized', value: 'startedSsvNotFinalized' },
    { label: 'NonPending SSV', value: 'nonPendingSsv' },
    { label: 'PendingNon SSV', value: 'pendingNonSsv' },
    { label: 'Lead Pending Verification', value: 'leadPendingVerification' },
    { label: 'Lead Pending Verification React', value: 'leadPendingVerificationReact' },
    { label: 'Lead At Verification Step 2 React', value: 'leadAtVerificationStep2React' },
    { label: 'Lead Pending Approval', value: 'leadPendingApproval' },
    { label: 'Lead Pending Approval React', value: 'leadPendingApprovalReact' },
    { label: 'Lead in Wire Queue', value: 'leadWireQueue' },
    { label: 'Lead in Dialer Verification Queue', value: 'leadDialerVerification' },
    { label: 'Customer with Withdrawn Lead for Express Loan', value: 'withdrawnLeadForExpressLoan' }
  ]

  const loanStates = [
    { label: 'Loan Origination Scheduled', value: 'loanOriginationScheduled' },
    { label: 'Loan in Good Standing', value: 'loanInGoodStanding' },
    { label: 'Loan in Good Standing with Reward Points', value: 'LoanInGoodStandingWithRewardPoints' },
    { label: 'Loan in Good Standing with Unclaimed Reward Points', value: 'LoanInGoodStandingWithUnclaimedRewardPoints' },
    { label: 'Loan in Good Standing with Expired Reward Points', value: 'LoanInGoodStandingWithExpiredRewardPoints' },
    { label: 'Loan in Good Standing React', value: 'loanInGoodStandingReact' },
    { label: 'Loan in Good Standing Match Excel', value: 'loanInGoodStandingMatchExcel' },
    { label: 'Loan in Good Standing No Bankruptcy', value: 'loanInGoodStandingNoBankruptcy' },
    { label: 'Loan in Delinquent', value: 'loanInDelinquent' },
    { label: 'Loan in Delinquent React', value: 'loanInDelinquentReact' },
    { label: 'Loan in Delinquent - Collections', value: 'loanInDelinquentCollections' },
    { label: 'Loan in Collection with No Payment Plan', value: 'loanInCollectionNoPaymentPlan' },
    { label: 'Loan in Collection', value: 'loanInCollection' },
    { label: 'Loan in Collection React', value: 'loanInCollectionReact' },
    { label: 'Loan in Settlement', value: 'loanInSettlement' },
    { label: 'Loan in Settlement React', value: 'loanInSettlementReact' },
    { label: 'Loan with Interest Free Payments Promo', value: 'loanWithInterestFreePromo' },
    { label: 'Loan with Free Payments Promo', value: 'loanWithFreePaymentsPromo' },
    { label: 'Loan with Free Loan Interest Promo', value: 'loanWithFreeLoanInterestPromo' },
    { label: 'Loan with Ability to Refinance', value: 'loanWithAbilityToRefinance' },
    { label: 'Loan with Winback Promo', value: 'loanWith50PercentOffPromo' },
    { label: 'Loan with Fresh Start Promo', value: 'loanWithFreshStartPromo' },
    { label: 'Loan with Collection Percent Offer Promo', value: 'loanWithCollectionPercentOffer' },
    { label: 'Loan with Confirmed BK', value: 'loanWithConfirmedBK' },
    { label: 'Loan with Dismissal BK', value: 'loanWithDismissBK' },
    { label: 'Loan with Discharge BK', value: 'loanWithDischargeBK' },
    { label: 'Loan with New Customer Survey', value: 'loanWithNewCustomerSurvey' },
    { label: 'Loan with Exit Survey', value: 'loanWithExitSurvey' },
    { label: 'Loan with React Survey', value: 'loanWithReactSurvey' },
  ]

  return (
    <LayoutSidebar
      id={false}
      contentTitle={"Test Data Generator"}
      content={(
        <div className="container m-0 p-2">
          <div className="columns is-multiline">
            {portfolioOptions.length > 0 && selectedPortfolio && (
              <div className="column is-one-third">
                <h3>Portfolios</h3>
                <div data-testid="test-setup-portfolio-select">
                  <Select
                    name="LeadStates"
                    classNames={reactSelectClassNames()}
                    options={portfolioOptions}
                    defaultValue={selectedPortfolio}
                    onChange={(e) => {
                      setSelectedPortfolio(e)
                    }}
                  />
                </div>
              </div>
            )}

            {payFrequencyOptions.length > 0 && selectedFrequency && (
              <div className="column is-one-third">
                <h3>Pay Frequency</h3>
                <div data-testid="test-setup-pay-frequency-select">
                  <Select
                    name="LeadStates"
                    classNames={reactSelectClassNames()}
                    options={payFrequencyOptions}
                    defaultValue={selectedFrequency}
                    onChange={(e) => {
                      setSelectedFrequency(e)
                    }}
                  />
                </div>
              </div>
            )}

            <div className="column is-one-third">
              <h3>Origination Date</h3>
              <input
                name="origination_date"
                data-testid="test-setup-origination-date"
                className="input is-small"
                type="date"
                onChange={(e) => {
                  setSelectedOriginationDate(e.target.value)
                }}
              />
            </div>

            <div className="column is-one-third">
              <h3>Loan Amount (optional)</h3>
              <input
                name="principal_amount"
                data-testid="test-setup-loan-amount"
                className="input is-small"
                type="number"
                min={principalRange?.min_principal ?? 1}
                max={principalRange?.max_principal ?? undefined}
                placeholder={
                  principalRange
                    ? `${principalRange.min_principal}–${principalRange.max_principal}`
                    : 'Leave blank for default'
                }
                value={selectedPrincipalAmount}
                onChange={(e) => {
                  setSelectedPrincipalAmount(e.target.value)
                }}
              />
            </div>

            <div className="column is-one-third">
              <h3>APR (optional)</h3>
              <input
                name="apr"
                data-testid="test-setup-apr"
                className="input is-small"
                type="number"
                step="0.01"
                min={principalRange?.min_apr ?? 0}
                max={principalRange?.max_apr ?? undefined}
                placeholder={
                  principalRange
                    ? `${principalRange.min_apr}–${principalRange.max_apr}`
                    : 'Leave blank for default'
                }
                value={selectedApr}
                onChange={(e) => {
                  setSelectedApr(e.target.value)
                }}
              />
            </div>
            {routingStatusOptions.length > 0 && selectedRoutingStatus && (
              <div className="column is-one-third">
                <h3>Routing Number Status</h3>
                <div data-testid="test-setup-routing-status">
                  <Select
                    name="RoutingNumberStatus"
                    classNames={reactSelectClassNames()}
                    options={routingStatusOptions}
                    defaultValue={selectedRoutingStatus}
                    onChange={(e) => {
                      setSelectedRoutingStatus(e)
                    }}
                  />
                </div>
              </div>
            )}
            <div className="column is-one-third">
              <h3>Reward Points (optional)</h3>
              <input
                name="reward_points"
                data-testid="test-setup-reward-points"
                className="input is-small"
                type="number"
                min={1}
                step={1}
                placeholder="Reward scenarios only; blank for random"
                value={selectedRewardPoints}
                onChange={(e) => {
                  setSelectedRewardPoints(e.target.value)
                }}
              />
            </div>
          </div>

          <hr />

          <h3>Create Test Lead</h3>
          <div data-testid="test-setup-lead-states-select">
            <Select
              name="LeadStates"
              classNames={reactSelectClassNames()}
              components={{ Option: ScenarioOption }}
              options={leadStates}
              onChange={(e) => {
                setLeadType(e.value)
              }}
            />
          </div>
          <br />
          <button
            data-testid="test-setup-create-lead-button"
            className="button is-link pr-4 pl-4"
            disabled={submitting}
            onClick={() => submitCreate('LeadStateTestSeeder')}
          >Create
          </button>
          <br /><br />
          <div data-testid="test-setup-create-loan-section">
            <h3>Create Test Loan</h3>
            <div data-testid="test-setup-loan-states-select">
              <Select
                name="LoanStates"
                classNames={reactSelectClassNames()}
                components={{ Option: ScenarioOption }}
                options={loanStates}
                onChange={(e) => {
                  setLoanType(e.value)
                }}
              />
            </div>
            <br />
            <button
              data-testid="test-setup-create-loan-button"
              className="button is-link pr-4 pl-4"
              disabled={submitting}
              onClick={() => submitCreate('LoanStateTestSeeder')}
            >Create
            </button>
          </div>
          <br /><br />
          <h3>Manual Cron Run</h3>
          <div data-testid="test-setup-cron-select">
            <AsyncSelect
              name="promotion_id"
              cacheOptions
              isSearchable={false}
              defaultOptions
              loadOptions={loadCrons}
              classNames={reactSelectClassNames()}
              onChange={(e) => {
                setCron(e)
              }}
            />
          </div>
          <p className="is-small is-italic">{cron?.description}</p>
          <br />
          <button
            data-testid="test-setup-run-cron-button"
            className="button is-link pr-4 pl-4"
            disabled={submitting || !cron}
            onClick={() => submitExecuteCron()}
          >Run
          </button>
          <br /><br />
          <h3>Sync Customers</h3>
          <textarea
            data-testid="test-setup-sync-customers-input"
            style={{
              padding: '2px',
              width: '308px',
              height: '100px',
            }}
            value={customerIds}
            placeholder="Customer IDs (Not guids). Separate multiple ID's with commas"
            onChange={(e) => {
              setSetCustomerIds(e.target.value)
              if (/^\s*(\d+\s*,\s*)*\d+\s*,?\s*$/.test(e.target.value)) {
                setError('')
              } else {
                setError('Please enter numbers separated by commas.')
              }
            }}
          />
          {error && <p className="has-text-danger">{error}</p>}
          <br />
          <button data-testid="test-setup-sync-customers-button" className="button is-link pr-4 pl-4" disabled={submitting} onClick={() => syncCustomers()}>Run</button>
        </div>
      )}
      dashlets={false}
    />
  )
}
